/**
 * De gegevens van het typepalet: welke typen en groepen horen bij het actieve
 * diagramtype. Gedeeld door het typepaneel en het pen-menu, zodat die altijd
 * dezelfde keuze tonen.
 */
import type { ElementType } from "@ae/domain";
import { useDeferredValue, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { allowedTypeIdsForViewKind, getRegistry, resolveBilingual } from "../../registry";
import { useDisplayMode } from "../../state/displayMode";
import { useWorkspaceData } from "../../state/workspace";
import { displayModePaletteAdjustments } from "./displayModePalette";

export const LAYER_ORDER: string[] = [
  "strategy",
  // ArchiMate 4 Common Domain — alleen zichtbaar in v4-weergavemodus; in
  // v3.1-modus verbergt `displayModePaletteAdjustments` deze types en
  // synthetiseert 't de laag-specifieke varianten terug.
  "common",
  "business",
  "application",
  "technology",
  "physical",
  "implementation_migration",
  "motivation",
  "composite",
];

/**
 * Eén regel in een laag-groep. Meestal is dat gewoon een actief element-type,
 * maar in v3.1-weergavemodus kan 't ook een SYNTHETISCHE 3.1-entry zijn: die
 * toont het oude type (label/laag/kleur/icoon) terwijl de drag het generieke
 * v4-type plus het compat-stereotype meegeeft. Zie displayModePalette.ts.
 */
/** Layer id → i18n key of its palette section title. */
export const LAYER_LABELS: Record<string, string> = {
  strategy: "palette.layers.strategy",
  common: "palette.layers.common",
  business: "palette.layers.business",
  application: "palette.layers.application",
  technology: "palette.layers.technology",
  physical: "palette.layers.physical",
  implementation_migration: "palette.layers.implementationMigration",
  motivation: "palette.layers.motivation",
  composite: "palette.layers.composite",
};

export interface PaletteRow {
  elementType: ElementType;
  /** Afwijkend type-id om aan te maken; leeg = `elementType.id`. */
  dragTypeId?: string;
  /** Stereotypes die de drop automatisch toepast. */
  defaultStereotypes?: string[];
}

export function usePaletteData({
  activeViewKind,
  unconstrained = false,
  activeFilter = null,
}: {
  activeViewKind: string | null;
  unconstrained?: boolean;
  activeFilter?: string | null;
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const revision = useDeferredValue(useWorkspaceData((s) => s.revision));
  const displayMode = useDisplayMode((s) => s.mode);
  // Resolve which types are allowed on the active diagram's viewpoint.
  // `null` means "no restriction" (e.g. archimate.anything).
  const allowedTypes = useMemo(() => {
    void revision;
    if (!activeViewKind) return null;
    return allowedTypeIdsForViewKind(activeViewKind);
  }, [activeViewKind, revision]);

  // Sharp / C4 / BMC-views tonen GEEN ArchiMate-laag-groepen in het
  // palette — de modelleur werkt daar zuiver via de bijbehorende
  // model-specifieke palette-group (DATA MODELING / C4 MODEL / BMC).
  // Symmetrisch: één view, één perspectief. Voor Sharp Data-Process
  // Connection wordt BusinessProcess via een per-entry view-filter
  // alsnog in DATA MODELING gedropt.
  //
  // Uitzonderingen:
  //  - `bmc.realization` — cross-extensie-bridging-view; daar mag de
  //    modelleur Capability/Resource/BusinessService (ArchiMate
  //    laag-groepen) én Concept (DATA MODELING) én C4 Container
  //    (C4 MODEL) selecteren om de BMC-blokken aan de realisatie
  //    te koppelen.
  //  - `bmc.evolution` — temporele BMC-view; vereist Plateau / Gap /
  //    WorkPackage (Implementation-laag) plus (in M7+) ConceptState
  //    via Sharp. Layer-groepen mogen dus zichtbaar zijn zodat
  //    Plateau-keten + transities modelleerbaar zijn.
  const BMC_LAYER_GROUP_EXCEPTIONS = new Set(["bmc.realization", "bmc.evolution"]);
  const suppressLayerGroups =
    // M6.5-W3b — Base-filter op sharp/c4/bmc verbergt de ArchiMate-laag-
    // groepen (anders zou je in een C4-folder-modus alsnog
    // BusinessActor/ApplicationComponent etc. zien staan).
    (activeFilter !== null && activeFilter !== "archimate") ||
    (!BMC_LAYER_GROUP_EXCEPTIONS.has(activeViewKind ?? "") &&
      ((activeViewKind?.startsWith("sharp.") ?? false) ||
        (activeViewKind?.startsWith("c4.") ?? false) ||
        (activeViewKind?.startsWith("bmc.") ?? false)));

  // Canvas-mode views (bv. bmc.canvas met layout_mode=canvas) hebben een
  // strikte slot-grid waarin alleen specifieke stereotypes mogen landen.
  // Universele palette-groepen zonder `applies_to_view_kinds` (bv.
  // Drawing-primitieven) horen daar niet — een vrije rechthoek of lijn
  // kan nergens neergezet worden in een canvas-grid. Dus op canvas-mode
  // views vervalt de "geen-restrictie = altijd tonen"-regel; alleen
  // palette-groepen die expliciet voor de actieve view bedoeld zijn
  // verschijnen.
  const isCanvasMode = useMemo(() => {
    void revision;
    if (!activeViewKind) return false;
    return getRegistry().getViewKind(activeViewKind)?.layout_mode === "canvas";
  }, [activeViewKind, revision]);

  const grouped = useMemo(() => {
    if (suppressLayerGroups) return [];
    const reg = getRegistry();
    // ArchiMate-views: toon alle laag-groepen (incl. types die niet in
    // het view-kind's `included_types` staan) zodat de modelleur elk
    // ArchiMate-element kan oppakken. De drop-validatie blijft strikt
    // via de matrix + isElementTypeAllowedOnViewKind (= drop weigert
    // een ongeldig type), dus we openen alleen de palette-visibiliteit.
    // Voor C4/Sharp/BMC-views blijft suppressLayerGroups=true hierboven
    // de archimate-laag-groepen sowieso weg.
    const isArchimateView = activeViewKind?.startsWith("archimate.") ?? false;
    // Extensions die óók surface'n via een palette-group (zoals
    // c4.Person / c4.SoftwareSystemInstance / c4.ContainerInstance /
    // c4.InfrastructureNode) horen alleen in hun palette-group thuis,
    // niet ook in de parent's laag-groep — anders staan ze dubbel.
    // Reguliere types die in een palette-group voorkomen (bv.
    // archimate.ApplicationComponent als basis voor "Software System")
    // blijven WEL in hun laag-groep zichtbaar; alleen extensions
    // worden gededupliceerd. Extra: entries met `exclusive: true`
    // (bv. sharp.Attribute / sharp.Identifier) worden ALTIJD uit de
    // laag-groep gefilterd — die horen alleen in hun palette-group.
    const extensionIds = new Set(reg.listExtensionIds());
    const inPaletteGroups = new Set<string>();
    const exclusiveInPaletteGroups = new Set<string>();
    for (const g of reg.listPaletteGroups()) {
      for (const e of g.entries) {
        inPaletteGroups.add(e.type_id);
        if (e.exclusive) exclusiveInPaletteGroups.add(e.type_id);
      }
    }
    // ArchiMate-weergavemodus (fase 3 van de v4-migratie). Vóór de flip is
    // dit een no-op in béide standen — zie displayModePalette.ts.
    const { hiddenTypeIds, legacyEntriesByLayer } = displayModePaletteAdjustments(reg, displayMode);
    const types = reg
      .listActiveElementTypes()
      .filter((t) => isArchimateView || allowedTypes === null || allowedTypes.has(t.id))
      .filter((t) => !(extensionIds.has(t.id) && inPaletteGroups.has(t.id)))
      .filter((t) => !exclusiveInPaletteGroups.has(t.id))
      .filter((t) => !hiddenTypeIds.has(t.id));
    const byLayer = new Map<string, PaletteRow[]>();
    for (const t of types) {
      const arr = byLayer.get(t.layer) ?? [];
      arr.push({ elementType: t });
      byLayer.set(t.layer, arr);
    }
    for (const [layer, entries] of legacyEntriesByLayer) {
      const arr = byLayer.get(layer) ?? [];
      for (const e of entries) {
        // Zelfde view-kind-filter als voor echte types: de synthetische
        // entry maakt straks een concept van `dragTypeId`, dus die id
        // bepaalt of 't op de actieve view mag.
        if (!(isArchimateView || allowedTypes === null || allowedTypes.has(e.dragTypeId))) continue;
        arr.push({
          elementType: e.elementType,
          dragTypeId: e.dragTypeId,
          defaultStereotypes: e.defaultStereotypes,
        });
      }
      byLayer.set(layer, arr);
    }
    return LAYER_ORDER.filter((l) => byLayer.has(l)).map((layer) => ({
      layer,
      types: (byLayer.get(layer) ?? []).sort((a, b) =>
        resolveBilingual(a.elementType.label, lang).localeCompare(
          resolveBilingual(b.elementType.label, lang),
        ),
      ),
    }));
  }, [lang, allowedTypes, suppressLayerGroups, activeViewKind, displayMode]);

  // Virtual palette groups from packs (currently the C4, DATA MODELING
  // en drawing groups). Filtered op twee niveaus:
  //  1. Group-niveau: applies_to_view_kinds — wanneer gezet en de
  //     actieve view-kind staat er niet bij, hele groep verbergen.
  //     Voorkomt dat C4-MODEL op een Sharp Contextual diagram
  //     verschijnt enkel omdat C4-Person (BusinessActor) en
  //     C4-Boundary (Grouping) toevallig de per-entry filter passeren.
  //  2. Entry-niveau: per entry op `allowedTypes` (de via-included_types
  //     of -visible_layers verzamelde set). Zonder restrictie =
  //     `allowedTypes === null` (vrij-vorm view).
  const paletteGroups = useMemo(() => {
    void revision;
    const reg = getRegistry();
    return reg
      .listPaletteGroups()
      .filter((group) => {
        // M6.5-W3b — in Base-WP-modus tonen we alle groepen ongefilterd
        // (unconstrained), tenzij de Foldertree-filter actief is — dan
        // alleen palette-groups van die folder-class. ArchiMate-laag-
        // groepen worden auto-gegenereerd (niet via listPaletteGroups),
        // dus dit raakt alleen de virtual c4/sharp/bmc/drawing-groepen.
        if (unconstrained) {
          if (activeFilter === null) return true;
          return (group.folder_class ?? "archimate") === activeFilter;
        }
        const allow = group.applies_to_view_kinds;
        // Geen restrictie → meestal tonen (Drawing-pattern). Uitzondering:
        // op canvas-mode views (bmc.canvas etc.) hebben universele
        // groepen geen plek — er is geen vrije plaatsing, alleen
        // slot-routing. Hide ze daar.
        if (!allow || allow.length === 0) return !isCanvasMode;
        // Met restrictie + geen actief diagram: NIET tonen. Voorheen
        // toonden we 'm alsnog ("toon alles wanneer onbekend") maar
        // dat verraste op archimate-context — modelleur zag C4 MODEL
        // staan terwijl 'ie geen C4-view voorhad. Pas zichtbaar zodra
        // een matching view-kind actief is.
        if (!activeViewKind) return false;
        return allow.includes(activeViewKind);
      })
      .map((group) => ({
        ...group,
        entries: group.entries.filter((entry) => {
          // M6.5-W3b — in Base-WP-modus alle entries doorlaten
          // (matchend met de group-niveau-ungating).
          if (unconstrained) return true;
          // Per-entry view-kind-filter — entries kunnen restrictiever
          // zijn dan hun groep (bv. BusinessProcess in DATA MODELING
          // alleen op sharp.data-process-connection).
          const entryAllow = entry.applies_to_view_kinds;
          if (entryAllow && entryAllow.length > 0) {
            if (!activeViewKind || !entryAllow.includes(activeViewKind)) {
              return false;
            }
          }
          // Per-entry uitsluitingen — voor specifieke views verbergen
          // ook al staat het base-type in included_types. Bv.
          // Component / Boundaries die op c4.container niet horen.
          const entryExclude = entry.excluded_view_kinds;
          if (
            entryExclude &&
            entryExclude.length > 0 &&
            activeViewKind &&
            entryExclude.includes(activeViewKind)
          ) {
            return false;
          }
          return allowedTypes === null || allowedTypes.has(entry.type_id);
        }),
      }))
      .filter((group) => group.entries.length > 0);
  }, [allowedTypes, activeViewKind, revision, isCanvasMode, unconstrained, activeFilter]);

  return { grouped, paletteGroups };
}
